import React from 'react';
import { Form, Input, Modal, Select } from 'antd';
import { number_to_price } from 'helper/common';
import { SERVICE_TYPES, getDerivedParentServiceType } from 'constants/StationServicesConstant';

const NUMERIC_INPUT_ALLOWED_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

const buildServiceCode = (serviceName = '') => {
  const serviceCode = String(serviceName || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/đ/g, 'd')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return serviceCode || `service-${Date.now()}`;
};

const buildServiceNameRule = (services = [], editingService = null) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    if (/[\uD800-\uDFFF]|[\u2600-\u27BF]/.test(value)) {
      return Promise.reject(new Error('Tên dịch vụ không hợp lệ'));
    }

    const normalizedValue = String(value).trim().toLowerCase();
    const serviceCode = buildServiceCode(value);
    const normalizedServiceCode = serviceCode.replace(/-/g, '');
    const isDuplicatedName = (services || []).some((service) => {
      if (service.stationServicesId === editingService?.stationServicesId) {
        return false;
      }

      const existedServiceCode = buildServiceCode(service.serviceName);
      return (
        String(service.serviceName || '').trim().toLowerCase() === normalizedValue ||
        existedServiceCode === serviceCode ||
        existedServiceCode.replace(/-/g, '') === normalizedServiceCode
      );
    });

    if (isDuplicatedName) {
      return Promise.reject(new Error('Tên dịch vụ đã tồn tại'));
    }

    return Promise.resolve();
  },
});

const buildCurrencyMinValueRule = (message) => ({
  validator: (_, value) => {
    const numericValue = Number(String(value || '').replace(/\D/g, ''));
    if (numericValue === 0 || numericValue >= 1000) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(message));
  },
});

const buildServiceFormPayload = (values, current = {}) => {
  const isCustomService = !current?.stationServicesId || current?.serviceType === SERVICE_TYPES.STATION_CUSTOM_SERVICE;
  const payload = {
    serviceName: values.serviceName,
    parentServiceType: values.parentServiceType || getDerivedParentServiceType(current),
    servicePrice: Number(String(values.servicePrice || '').replace(/\D/g, '')),
    serviceBookingFee: Number(String(values.bookingFee || '').replace(/\D/g, '')),
  };

  if (isCustomService) {
    payload.serviceCode = buildServiceCode(values.serviceName);
  }

  return payload;
};

const handleNumericInput = (e) => {
  const char = e.key;
  if (!/\d/.test(char) && !NUMERIC_INPUT_ALLOWED_KEYS.includes(char)) {
    e.preventDefault();
  }
};

const handleNumericPaste = (e) => {
  const value = e.clipboardData.getData('text');
  if (!/^\d+$/.test(value)) {
    e.preventDefault();
  }
};

const ServiceSettingModal = ({
  visible,
  editingService,
  form,
  services,
  serviceCategoryOptions,
  confirmLoading,
  onCancel,
  onSave,
}) => {
  const handleCurrencyChange = (fieldName) => (e) => {
    form.setFieldsValue({
      [fieldName]: number_to_price(Number(String(e.target.value || '').replace(/\D/g, ''))),
    });
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    onSave(buildServiceFormPayload(values, editingService || {}));
  };

  return (
    <Modal
      visible={visible}
      title={editingService ? "Sửa dịch vụ" : "Thêm mới dịch vụ"}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={confirmLoading}
      okText="Lưu"
      cancelText="Hủy"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="serviceName"
          label="Tên dịch vụ"
          rules={[
            { required: true, message: 'Vui lòng nhập tên dịch vụ' },
            { max: 255, message: 'Tên dịch vụ không không được dài hơn 255 kí tự' },
            buildServiceNameRule(services, editingService),
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="parentServiceType"
          label="Phân loại"
          rules={[{ required: true, message: 'Vui lòng chọn phân loại' }]}
        >
          <Select options={serviceCategoryOptions} />
        </Form.Item>
        <Form.Item
          name="bookingFee"
          label="Phí đặt lịch"
          rules={[
            { required: true, message: 'Vui lòng nhập phí đặt lịch' },
            buildCurrencyMinValueRule('Phí đặt lịch phải bằng 0 hoặc từ 1.000đ trở lên'),
          ]}
        >
          <Input
            addonAfter="đ"
            onChange={handleCurrencyChange('bookingFee')}
            onKeyDown={handleNumericInput}
            onPaste={handleNumericPaste}
          />
        </Form.Item>
        <Form.Item
          name="servicePrice"
          label="Giá tiền"
          rules={[
            { required: true, message: 'Vui lòng nhập giá tiền' },
            buildCurrencyMinValueRule('Giá tiền phải bằng 0 hoặc từ 1.000đ trở lên'),
          ]}
        >
          <Input
            addonAfter="đ"
            onChange={handleCurrencyChange('servicePrice')}
            onKeyDown={handleNumericInput}
            onPaste={handleNumericPaste}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ServiceSettingModal;
